// «Активность пользователей» (только админ): по каждому серверу — сколько пользователей его добавили, сколько
// заходило в отчёты за 7 и 30 дней, последний заход; по нажатию — пользователи сервера. Админы не учитываются.
// Над списком — карточки итогов: пользователей, серверов, активных за 30 дней, заходов в день.
// Сразу видны серверы, где заходили за STALE_DAYS дней (свежие сверху), остальные (подсвечены) — под «Показать все».
// У сервера так же: пользователи за STALE_DAYS дней с датой и временем последнего захода, остальные — под кнопкой.
import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CollapsibleSection from './settings/CollapsibleSection';
import { ACCENT, StatusText, WARNING_COLOR } from './settings/SettingsControls';
import { useDesktopLayout } from './layout/AppLayout';
import { fetchUserActivity } from '../services/dataService';
import { extractErrorMessage } from '../utils/errors';
import {
  STALE_DAYS, formatDay, isStale, lastSeenText, lastVisitText, splitMembers, splitServers, summaryCards,
} from '../utils/userActivity';
import { CARD_LABEL_COLOR, DIVIDER_COLOR, MUTED_TEXT_COLOR } from '../styles/tokens';

const COLUMNS = 'minmax(0, 1fr) 104px 64px 64px 128px 24px';
const NUMERIC_SX = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

export default function UserActivityAdmin({ token, sx }) {
  return (
    <CollapsibleSection id="user-activity" title="Активность пользователей" sx={sx}>
      <UserActivity token={token} />
    </CollapsibleSection>
  );
}

function UserActivity({ token }) {
  const desktop = useDesktopLayout();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      setReport(await fetchUserActivity(token));
      setStatus(null);
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { recent, rest } = report ? splitServers(report.servers, report.today) : { recent: [], rest: [] };
  const servers = showAll ? [...recent, ...rest] : recent;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR, minWidth: 0 }}>
          Дни, когда пользователи открывали отчёты сервера. Без админов, за последние {report?.keep_days || 180} дней.
        </Typography>
        <Tooltip title="Обновить">
          <span>
            <IconButton onClick={load} disabled={loading} aria-label="Обновить" sx={{ width: 44, height: 44, flexShrink: 0, color: CARD_LABEL_COLOR }}>
              {loading ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <StatusText status={status} sx={{ mb: 1 }} />

      {report?.summary && <SummaryCards summary={report.summary} />}

      {report && recent.length + rest.length === 0 && (
        <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.55)' }}>Серверов пока нет.</Typography>
      )}
      {report && recent.length === 0 && rest.length > 0 && !showAll && (
        <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.55)' }}>{`За ${STALE_DAYS} дней никто не заходил.`}</Typography>
      )}

      {servers.length > 0 && (
        <Box role="list" aria-label="Серверы">
          {desktop && (
            <Box aria-hidden="true" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1.5, px: '10px', pb: 0.5, fontSize: '13px', color: MUTED_TEXT_COLOR }}>
              <span>Сервер</span>
              <Box sx={NUMERIC_SX}>Пользователей</Box>
              <Box sx={NUMERIC_SX}>7 дн</Box>
              <Box sx={NUMERIC_SX}>30 дн</Box>
              <Box sx={NUMERIC_SX}>Последний заход</Box>
              <span />
            </Box>
          )}
          {servers.map((server) => (
            <ServerRow
              key={server.server_key}
              server={server}
              today={report.today}
              desktop={desktop}
              open={openKey === server.server_key}
              onToggle={() => setOpenKey((key) => (key === server.server_key ? null : server.server_key))}
            />
          ))}
        </Box>
      )}

      {rest.length > 0 && <ShowAllButton open={showAll} count={rest.length} onClick={() => setShowAll((v) => !v)} sx={{ mt: 0.5 }} />}
    </Box>
  );
}

function SummaryCards({ summary }) {
  return (
    <Box
      component="dl"
      sx={{ m: 0, mb: 2, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}
    >
      {summaryCards(summary).map((card) => (
        <Box
          key={card.key}
          sx={{ p: '12px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${DIVIDER_COLOR}`, minWidth: 0 }}
        >
          <Typography component="dt" sx={{ fontSize: '13px', color: CARD_LABEL_COLOR }}>{card.label}</Typography>
          <Typography component="dd" sx={{ m: 0, mt: 0.5, fontSize: '24px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {card.value}
          </Typography>
          <Typography component="dd" sx={{ m: 0, mt: 0.25, fontSize: '12px', color: MUTED_TEXT_COLOR }}>{card.hint}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function ShowAllButton({ open, count, onClick, sx }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-expanded={open}
      sx={{ minHeight: 44, px: 1, borderRadius: '8px', fontWeight: 600, fontSize: '14px', color: ACCENT,
        '&.Mui-focusVisible': { outline: `2px solid ${ACCENT}`, outlineOffset: '2px' }, ...sx }}
    >
      {open ? 'Скрыть' : `Показать все (${count})`}
    </ButtonBase>
  );
}

function ServerRow({ server, today, desktop, open, onToggle }) {
  const stale = isStale(server.last_day, today);
  const last = lastVisitText(server.last_day, today);
  const lastSx = { fontSize: '14px', color: stale ? WARNING_COLOR : '#fff', fontWeight: stale ? 600 : 400 };
  const chevron = (
    <ExpandMoreIcon
      aria-hidden="true"
      sx={{
        color: MUTED_TEXT_COLOR,
        transition: 'transform 0.2s ease',
        transform: open ? 'rotate(180deg)' : 'none',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    />
  );

  return (
    <Box role="listitem" sx={{ borderTop: `1px solid ${DIVIDER_COLOR}` }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${server.name}: пользователей ${server.users}, за 7 дней ${server.active_7}, за 30 дней ${server.active_30}, последний заход ${last}`}
        sx={{
          width: '100%',
          minHeight: 56,
          boxSizing: 'border-box',
          p: '10px',
          borderRadius: '10px',
          textAlign: 'left',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
          '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${ACCENT}` },
          ...(desktop
            ? { display: 'grid', gridTemplateColumns: COLUMNS, gap: 1.5, alignItems: 'center' }
            : { display: 'flex', alignItems: 'center', gap: 1 }),
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>{server.name}</Typography>
          <Typography noWrap sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>
            {desktop
              ? server.server_key
              : `Пользователей ${server.users} · за 7 дн ${server.active_7} · за 30 дн ${server.active_30}`}
          </Typography>
        </Box>
        {desktop ? (
          <>
            <Typography sx={{ ...NUMERIC_SX, fontSize: '14px', color: '#fff' }}>{server.users}</Typography>
            <Typography sx={{ ...NUMERIC_SX, fontSize: '14px', color: '#fff' }}>{server.active_7}</Typography>
            <Typography sx={{ ...NUMERIC_SX, fontSize: '14px', color: '#fff' }}>{server.active_30}</Typography>
            <Typography sx={{ ...NUMERIC_SX, ...lastSx }}>{last}</Typography>
          </>
        ) : (
          <Typography sx={{ ...NUMERIC_SX, ...lastSx, flexShrink: 0 }}>{last}</Typography>
        )}
        {chevron}
      </ButtonBase>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Members members={server.members} today={today} />
      </Collapse>
    </Box>
  );
}

function Members({ members, today }) {
  const [showAll, setShowAll] = useState(false);
  if (members.length === 0) {
    return <Typography sx={{ px: '10px', pb: 1.5, fontSize: '13px', color: MUTED_TEXT_COLOR }}>Пользователей нет.</Typography>;
  }
  const { recent, rest } = splitMembers(members, today);
  const shown = showAll ? [...recent, ...rest] : recent;
  return (
    <Box sx={{ px: '10px', pb: 1.5 }}>
      {recent.length === 0 && !showAll && (
        <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>{`За ${STALE_DAYS} дней никто не заходил.`}</Typography>
      )}
      {shown.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {shown.map((m) => <MemberRow key={m.user_id} member={m} today={today} />)}
        </Box>
      )}
      {rest.length > 0 && <ShowAllButton open={showAll} count={rest.length} onClick={() => setShowAll((v) => !v)} sx={{ mt: 0.5, ml: -1 }} />}
    </Box>
  );
}

function MemberRow({ member: m, today }) {
  return (
    <Box component="li" sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', color: '#e4e4e7', wordBreak: 'break-word' }}>
          {m.name}
          {!m.added && <Box component="span" sx={{ color: MUTED_TEXT_COLOR }}> · сервер удалён</Box>}
        </Typography>
        {m.registered && (
          <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>{`в приложении с ${formatDay(m.registered)}`}</Typography>
        )}
      </Box>
      <Typography sx={{ ...NUMERIC_SX, fontSize: '13px', color: MUTED_TEXT_COLOR, flexShrink: 0 }}>
        {`${m.days_30} дн. из 30 · ${lastSeenText(m, today)}`}
      </Typography>
    </Box>
  );
}
